// ===============================================================
// Forms — Form Builder (3-panel layout)
// Toolbox (left) | Canvas (center) | Config (right)
// Drag-and-drop field ordering, inline editing, full CRUD
// ===============================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text, Paper, Group, Button, TextInput, Select, Textarea,
  ActionIcon, Stack, Badge, Switch, Tabs, SimpleGrid, Divider,
  ScrollArea, NumberInput, Loader, Center, Tooltip,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconArrowLeft, IconDeviceFloppy, IconRocket, IconEye,
  IconGripVertical, IconTrash, IconPlus, IconX, IconSettings,
  IconChevronDown, IconLink, IconCheck,
} from '@tabler/icons-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFormsStore } from '../../store/formsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { FIELD_TYPE_META, makeField, makeSchema } from '../../types/forms';
// import { useAuthStore } from '../../store/authStore';
import type { FormSchema, FormField, FormFieldType, FormPage } from '../../types/forms';

// ── Field type groups ────────────────────────────────────────
const GROUPS: Array<{ key: 'basic' | 'advanced' | 'layout'; label: string }> = [
  { key: 'basic', label: 'Basic Fields' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'layout', label: 'Layout' },
];

const FIELD_TYPES_BY_GROUP = GROUPS.map(g => ({
  ...g,
  types: (Object.entries(FIELD_TYPE_META) as Array<[FormFieldType, typeof FIELD_TYPE_META[FormFieldType]]>)
    .filter(([, meta]) => meta.group === g.key),
}));

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_empty', label: 'Is Not Empty' },
  { value: 'empty', label: 'Is Empty' },
];

// ── Sortable Field Component ─────────────────────────────────
function SortableField({ field, isSelected, onSelect, onRemove }: {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const meta = FIELD_TYPE_META[field.type];

  return (
    <Paper
      ref={setNodeRef}
      style={{
        ...style,
        cursor: 'pointer',
        border: isSelected ? '2px solid var(--mantine-color-blue-5)' : '1px solid var(--mantine-color-gray-3)',
      }}
      p="sm"
      mb="xs"
      radius="sm"
      onClick={onSelect}
    >
      <Group justify="space-between" gap="xs" mb={6}>
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            {...attributes}
            {...listeners}
            style={{ cursor: 'grab' }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconGripVertical size={14} />
          </ActionIcon>
          <Text size="sm" fw={500}>{field.label || 'Untitled Field'}</Text>
          {field.required && <Text size="xs" c="red" fw={700}>*</Text>}
        </Group>
        <Group gap={4}>
          <Badge size="xs" variant="light" color="gray">{meta?.label ?? field.type}</Badge>
          <ActionIcon
            variant="subtle"
            color="red"
            size="xs"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            <IconX size={12} />
          </ActionIcon>
        </Group>
      </Group>
      {/* Field preview */}
      <FieldPreview field={field} />
    </Paper>
  );
}

// ── Field Preview (disabled) ─────────────────────────────────
function FieldPreview({ field }: { field: FormField }) {
  const shared = { size: 'sm' as const, disabled: true, placeholder: field.placeholder || field.label };

  switch (field.type) {
    case 'text': case 'email': case 'phone': case 'url':
      return <TextInput {...shared} />;
    case 'number':
      return <NumberInput {...shared} />;
    case 'date':
      return <TextInput {...shared} type="date" />;
    case 'textarea':
      return <Textarea {...shared} minRows={2} />;
    case 'select':
      return (
        <Select
          {...shared}
          data={(field.options || []).map(o => ({ value: o.value, label: o.label }))}
          placeholder={field.placeholder || 'Select...'}
        />
      );
    case 'multiselect':
      return <TextInput {...shared} placeholder="Multi-select..." />;
    case 'checkbox':
      return (
        <Stack gap={4}>
          {((field.options || []).length > 0 ? (field.options || []) : [{ label: 'Option 1', value: '1' }]).map((o, i) => (
            <Group key={i} gap="xs"><Switch size="xs" disabled /><Text size="xs">{o.label}</Text></Group>
          ))}
        </Stack>
      );
    case 'radio':
      return (
        <Stack gap={4}>
          {((field.options || []).length > 0 ? (field.options || []) : [{ label: 'Option 1', value: '1' }]).map((o, i) => (
            <Group key={i} gap="xs">
              <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #adb5bd' }} />
              <Text size="xs">{o.label}</Text>
            </Group>
          ))}
        </Stack>
      );
    case 'rating':
      return <Group gap={4}>{[1,2,3,4,5].map(n => <Text key={n} size="lg" c="dimmed">&#9734;</Text>)}</Group>;
    case 'signature':
      return <Paper p="sm" withBorder style={{ height: 60, background: 'var(--mantine-color-gray-0)' }}><Text size="xs" c="dimmed">Signature area</Text></Paper>;
    case 'photo':
      return <Button size="xs" variant="light" disabled>Capture Photo</Button>;
    case 'file':
      return <Button size="xs" variant="light" disabled>Upload File</Button>;
    case 'gps':
      return <TextInput {...shared} placeholder="GPS coordinates" />;
    case 'likert':
      return <Text size="xs" c="dimmed" fs="italic">Likert scale grid</Text>;
    case 'section':
      return <Divider label={field.label || 'Section'} labelPosition="left" />;
    case 'instructions':
      return <Text size="xs" c="dimmed">{field.helpText || 'Instructions text...'}</Text>;
    case 'page_break':
      return <Divider variant="dashed" label="Page Break" labelPosition="center" />;
    default:
      return <TextInput {...shared} />;
  }
}

// ── Main Component ───────────────────────────────────────────
export default function FormBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const { user } = useAuthStore();
  const { canEdit } = useModulePermission('forms');
  const { templates, loadTemplates, createTemplate, updateTemplate } = useFormsStore();

  // Local state
  const [schema, setSchema] = useState<FormSchema>(makeSchema());
  const [templateRid, setTemplateRid] = useState<number | null>(null);
  const [templateStatus, setTemplateStatus] = useState<string>('draft');
  const [isPublic, setIsPublic] = useState(false);
  const [publicSlug, setPublicSlug] = useState('');
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Load template
  useEffect(() => {
    if (id && id !== 'new') {
      loadTemplates().then(() => setLoaded(true));
    } else {
      setLoaded(true);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loaded || !id || id === 'new') return;
    const tpl = templates.find(t => String(t.id) === id || String(t.Rid) === id);
    if (tpl) {
      try {
        const rawSchema = (tpl as any).schemaJson || (tpl as any).schema || '';
        const parsed = JSON.parse(rawSchema) as FormSchema;
        const pages = Array.isArray(parsed?.pages) && parsed.pages.length > 0
          ? parsed.pages
          : [{ title: 'Page 1', fields: [] }];
        setSchema({ ...makeSchema(), ...parsed, pages });
      } catch {
        setSchema(prev => ({ ...prev, title: (tpl as any).templateName || (tpl as any).title || prev.title }));
      }
      setTemplateRid((tpl as any).Rid || tpl.id);
      setTemplateStatus(tpl.status);
      // isPublic/publicSlug stored inside schema JSON blob (no dedicated DB fields)
      try {
        const raw = (tpl as any).schemaJson || (tpl as any).schema || '{}';
        const p = JSON.parse(raw);
        setIsPublic(p.isPublic === true || p.isPublic === 'true');
        setPublicSlug(String(p.publicSlug || ''));
      } catch {
        setIsPublic((tpl as any).isPublic === 'true');
        setPublicSlug((tpl as any).publicSlug || '');
      }
    }
  }, [loaded, templates, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Current page — guard against empty pages array (e.g. stub templates with no fields)
  const safePages = Array.isArray(schema?.pages) && schema.pages.length > 0
    ? schema.pages
    : [{ title: 'Page 1', fields: [] }];
  const currentPage: FormPage = safePages[currentPageIdx] ?? safePages[0];
  const fields = currentPage?.fields ?? [];

  // All fields across all pages (for conditional logic dropdowns)
  const allFields = useMemo(() => safePages.flatMap(p => p.fields ?? []), [schema]);

  // Selected field
  const selectedField = selectedFieldKey ? fields.find(f => f.key === selectedFieldKey) ?? null : null;

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ── Schema mutation helpers ────────────────────────────────
  const updatePage = useCallback((pageIdx: number, updater: (page: FormPage) => FormPage) => {
    setSchema(prev => ({
      ...prev,
      pages: prev.pages.map((p, i) => i === pageIdx ? updater(p) : p),
    }));
  }, []);

  const updateFieldInPage = useCallback((fieldKey: string, updater: (f: FormField) => FormField) => {
    updatePage(currentPageIdx, page => ({
      ...page,
      fields: page.fields.map(f => f.key === fieldKey ? updater(f) : f),
    }));
  }, [currentPageIdx, updatePage]);

  // Add field from toolbox
  const addField = useCallback((type: FormFieldType) => {
    const newField = makeField(type);
    const meta = FIELD_TYPE_META[type];
    newField.label = meta.label;
    // Add default options for choice types — generate stable ids so renaming
    // labels later doesn't orphan past responses
    const newOptId = () => `opt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    if (['select', 'multiselect', 'radio', 'checkbox'].includes(type)) {
      newField.options = [
        { label: 'Option 1', value: newOptId() },
        { label: 'Option 2', value: newOptId() },
      ];
    }
    if (type === 'likert') {
      newField.options = [
        { label: 'Strongly Disagree', value: '1' },
        { label: 'Disagree', value: '2' },
        { label: 'Neutral', value: '3' },
        { label: 'Agree', value: '4' },
        { label: 'Strongly Agree', value: '5' },
      ];
    }
    updatePage(currentPageIdx, page => ({
      ...page,
      fields: [...page.fields, newField],
    }));
    setSelectedFieldKey(newField.key);
  }, [currentPageIdx, updatePage]);

  // Remove field
  const removeField = useCallback((key: string) => {
    updatePage(currentPageIdx, page => ({
      ...page,
      fields: page.fields.filter(f => f.key !== key),
    }));
    if (selectedFieldKey === key) setSelectedFieldKey(null);
  }, [currentPageIdx, updatePage, selectedFieldKey]);

  // Drag end
  const handleDragEnd = useCallback((event: { active: { id: string | number }; over: { id: string | number } | null }) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updatePage(currentPageIdx, page => {
      const oldIdx = page.fields.findIndex(f => f.key === active.id);
      const newIdx = page.fields.findIndex(f => f.key === over.id);
      if (oldIdx === -1 || newIdx === -1) return page;
      return { ...page, fields: arrayMove(page.fields, oldIdx, newIdx) };
    });
  }, [currentPageIdx, updatePage]);

  // Page management
  const addPage = () => {
    setSchema(prev => ({
      ...prev,
      pages: [...prev.pages, { title: `Page ${prev.pages.length + 1}`, fields: [] }],
    }));
    setCurrentPageIdx(schema.pages.length);
    setSelectedFieldKey(null);
  };

  const removePage = (idx: number) => {
    if (schema.pages.length <= 1) return;
    setSchema(prev => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== idx),
    }));
    if (currentPageIdx >= schema.pages.length - 1) {
      setCurrentPageIdx(Math.max(0, schema.pages.length - 2));
    }
    setSelectedFieldKey(null);
  };

  // ── Save / Publish ────────────────────────────────────────
  const handleSave = async (status?: string) => {
    setSaving(true);
    const targetStatus = status ?? templateStatus;
    try {
      // Generate slug if making public for the first time
      let slug = publicSlug;
      if (isPublic && !slug) {
        slug = (schema.title || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + Math.random().toString(36).slice(2, 6);
        setPublicSlug(slug);
      }

      // Embed isPublic/publicSlug in schema blob (no dedicated DB fields)
      const schemaJson = JSON.stringify({ ...schema, isPublic, publicSlug: slug });

      if (templateRid) {
        await updateTemplate(templateRid, {
          title: schema.title,
          description: schema.description,
          schemaJson,
          status: targetStatus,
        });
        setTemplateStatus(targetStatus);
      } else {
        const rid = await createTemplate({
          title: schema.title,
          description: schema.description,
          schemaJson,
          status: targetStatus,
        });
        setTemplateRid(rid);
        setTemplateStatus(targetStatus);
        // Update URL to include the new ID
        navigate(`/forms/builder/${rid}`, { replace: true });
      }
      notifications.show({
        title: targetStatus === 'published' ? 'Published' : 'Saved',
        message: targetStatus === 'published' ? 'Form is now live' : 'Draft saved',
        color: targetStatus === 'published' ? 'green' : 'blue',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Options list editor ───────────────────────────────────
  // Options editor uses local state to avoid losing focus on every keystroke
  const [localOpts, setLocalOpts] = useState<{ label: string; value: string }[]>([]);
  const [localOptsFieldKey, setLocalOptsFieldKey] = useState('');

  const syncLocalOpts = useCallback((field: FormField) => {
    if (field.key !== localOptsFieldKey) {
      setLocalOpts((field.options || []).map(o => ({ ...o })));
      setLocalOptsFieldKey(field.key);
    }
  }, [localOptsFieldKey]);

  const commitOpts = useCallback((fieldKey: string) => {
    updateFieldInPage(fieldKey, f => ({ ...f, options: localOpts }));
  }, [localOpts, updateFieldInPage]);

  const OptionsEditor = ({ field }: { field: FormField }) => {
    // Sync local state when field changes
    if (field.key !== localOptsFieldKey) {
      syncLocalOpts(field);
      return null; // re-render with correct local state
    }
    return (
      <Stack gap="xs">
        {localOpts.map((opt, i) => (
          <Group key={`opt-${field.key}-${i}`} gap="xs">
            <TextInput
              size="xs"
              style={{ flex: 1 }}
              defaultValue={opt.label}
              placeholder={`Option ${i + 1}`}
              onBlur={(e) => {
                const val = e.target.value;
                setLocalOpts(prev => {
                  const next = prev.map((o, j) =>
                    // Keep the original `value` (stable id) — only update display label
                    j === i ? { ...o, label: val } : o
                  );
                  return next;
                });
                commitOpts(field.key);
              }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => {
                const updated = localOpts.filter((_, j) => j !== i);
                setLocalOpts(updated);
                updateFieldInPage(field.key, f => ({ ...f, options: updated }));
              }}
              disabled={localOpts.length <= 1}
            >
              <IconX size={12} />
            </ActionIcon>
          </Group>
        ))}
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconPlus size={12} />}
          onClick={() => {
            const n = localOpts.length + 1;
            // Generate a stable id so renaming the label doesn't orphan past responses
            const stableId = `opt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
            const updated = [...localOpts, { label: `Option ${n}`, value: stableId }];
            setLocalOpts(updated);
            updateFieldInPage(field.key, f => ({ ...f, options: updated }));
          }}
        >
          Add Option
        </Button>
      </Stack>
    );
  };

  if (!loaded) {
    return <Center p="xl"><Loader /></Center>;
  }

  // ═════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════
  return (
    <div>
      {/* ── Top Bar ──────────────────────────────────────────── */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/forms/templates')}
          >
            Templates
          </Button>
          <Text fw={600} size="sm">{schema.title || 'Untitled Form'}</Text>
          <Badge
            size="sm"
            color={templateStatus === 'published' ? 'green' : templateStatus === 'archived' ? 'gray' : 'orange'}
          >
            {templateStatus}
          </Badge>
        </Group>
        <Group gap="xs">
          {canEdit && (
            <>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconDeviceFloppy size={14} />}
                onClick={() => handleSave()}
                loading={saving}
              >
                Save Draft
              </Button>
              <Button
                size="xs"
                color="green"
                leftSection={<IconRocket size={14} />}
                onClick={() => handleSave('published')}
                loading={saving}
              >
                Publish
              </Button>
              <Switch
                label="Public"
                size="xs"
                checked={isPublic}
                onChange={e => setIsPublic(e.currentTarget.checked)}
                styles={{ label: { fontSize: 11, paddingLeft: 4 } }}
              />
            </>
          )}
          {!canEdit && (
            <Badge color="gray" variant="light" size="lg">View Only</Badge>
          )}
          {isPublic && publicSlug && templateStatus === 'published' && (
            <Button
              variant="light"
              color="violet"
              size="xs"
              leftSection={linkCopied ? <IconCheck size={14} /> : <IconLink size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/forms/public/${publicSlug}`);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
              }}
            >
              {linkCopied ? 'Copied!' : 'Copy Link'}
            </Button>
          )}
          {templateRid && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconEye size={14} />}
              onClick={() => navigate(`/forms/preview/${templateRid}`)}
            >
              Preview
            </Button>
          )}
        </Group>
      </Group>

      {/* ── 3-Column Grid ────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 260px', gap: 12, height: 'calc(100vh - 180px)' }}>

        {/* ── LEFT: Field Toolbox ──────────────────────────── */}
        <Paper p="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Field Toolbox</Text>
          <ScrollArea h="calc(100vh - 260px)">
            {FIELD_TYPES_BY_GROUP.map(group => (
              <div key={group.key}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4} mt="sm">{group.label}</Text>
                <Stack gap={4}>
                  {group.types.map(([type, meta]) => (
                    <Paper
                      key={type}
                      p="xs"
                      withBorder
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onClick={() => addField(type)}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-blue-0)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                    >
                      <Group gap="xs">
                        <Text size="sm" style={{ width: 20, textAlign: 'center' }}>{meta.icon}</Text>
                        <Text size="xs">{meta.label}</Text>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </div>
            ))}
          </ScrollArea>
        </Paper>

        {/* ── CENTER: Canvas ───────────────────────────────── */}
        <Paper p="md" withBorder>
          {/* Form title + description (inline editing) */}
          <TextInput
            variant="unstyled"
            size="xl"
            fw={700}
            value={schema.title}
            onChange={(e) => setSchema(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Form Title"
            styles={{ input: { fontSize: 22, fontWeight: 700 } }}
            mb={4}
          />
          <Textarea
            variant="unstyled"
            size="sm"
            value={schema.description}
            onChange={(e) => setSchema(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Form description (optional)"
            autosize
            minRows={1}
            mb="md"
            styles={{ input: { color: 'var(--mantine-color-dimmed)' } }}
          />

          {/* Page tabs */}
          <Group justify="space-between" mb="sm">
            <Tabs
              value={String(currentPageIdx)}
              onChange={(v) => { setCurrentPageIdx(Number(v)); setSelectedFieldKey(null); }}
            >
              <Tabs.List>
                {schema.pages.map((page, i) => (
                  <Tabs.Tab key={i} value={String(i)} rightSection={
                    schema.pages.length > 1 ? (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size={14}
                        onClick={(e) => { e.stopPropagation(); removePage(i); }}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    ) : null
                  }>
                    <Text size="xs">{page.title}</Text>
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs>
            <Tooltip label="Add page">
              <ActionIcon variant="light" size="sm" onClick={addPage}>
                <IconPlus size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Page title editing */}
          <TextInput
            size="xs"
            variant="filled"
            value={currentPage?.title ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              updatePage(currentPageIdx, p => ({ ...p, title: val }));
            }}
            placeholder="Page title"
            mb="sm"
            styles={{ input: { fontWeight: 500 } }}
          />

          {/* Fields list */}
          <ScrollArea h="calc(100vh - 340px)">
            {fields.length === 0 ? (
              <Paper
                p="xl"
                withBorder
                style={{
                  borderStyle: 'dashed',
                  textAlign: 'center',
                  background: 'var(--mantine-color-gray-0)',
                }}
              >
                <IconChevronDown size={28} color="var(--mantine-color-gray-5)" style={{ marginBottom: 8 }} />
                <Text size="sm" c="dimmed">
                  Click a field type from the toolbox to add it here
                </Text>
              </Paper>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fields.map(f => f.key)} strategy={verticalListSortingStrategy}>
                  {fields.map(field => (
                    <SortableField
                      key={field.key}
                      field={field}
                      isSelected={selectedFieldKey === field.key}
                      onSelect={() => setSelectedFieldKey(field.key)}
                      onRemove={() => removeField(field.key)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </ScrollArea>
        </Paper>

        {/* ── RIGHT: Config Panel ──────────────────────────── */}
        <Paper p="sm" withBorder>
          {selectedField ? (
            <ScrollArea h="calc(100vh - 260px)">
              <Group gap="xs" mb="sm">
                <IconSettings size={16} />
                <Text fw={600} size="sm">Field Settings</Text>
              </Group>

              {/* 1. Basic settings */}
              <Stack gap="xs" mb="md">
                <TextInput
                  label="Label"
                  size="xs"
                  value={selectedField.label}
                  onChange={(e) => updateFieldInPage(selectedField.key, f => ({ ...f, label: e.target.value }))}
                />
                <TextInput
                  label="Placeholder"
                  size="xs"
                  value={selectedField.placeholder}
                  onChange={(e) => updateFieldInPage(selectedField.key, f => ({ ...f, placeholder: e.target.value }))}
                />
                <Textarea
                  label="Help Text"
                  size="xs"
                  value={selectedField.helpText}
                  onChange={(e) => updateFieldInPage(selectedField.key, f => ({ ...f, helpText: e.target.value }))}
                  minRows={2}
                  autosize
                />
                <Select
                  label="Field Type"
                  size="xs"
                  value={selectedField.type}
                  data={Object.entries(FIELD_TYPE_META).map(([val, meta]) => ({
                    value: val,
                    label: `${meta.icon} ${meta.label}`,
                  }))}
                  onChange={(val) => {
                    if (!val) return;
                    updateFieldInPage(selectedField.key, f => ({ ...f, type: val as FormFieldType }));
                  }}
                />
              </Stack>

              {/* 2. Options (for choice types) */}
              {['select', 'multiselect', 'radio', 'checkbox', 'likert'].includes(selectedField.type) && (
                <>
                  <Divider mb="sm" />
                  <Text fw={600} size="xs" mb="xs">Options</Text>
                  <OptionsEditor field={selectedField} />
                </>
              )}

              {/* 3. Validation */}
              <Divider my="sm" />
              <Text fw={600} size="xs" mb="xs">Validation</Text>
              <Stack gap="xs">
                <Switch
                  label="Required"
                  size="xs"
                  checked={selectedField.required}
                  onChange={(e) => updateFieldInPage(selectedField.key, f => ({ ...f, required: e.currentTarget.checked }))}
                />
                {['text', 'textarea', 'email', 'url', 'phone'].includes(selectedField.type) && (
                  <SimpleGrid cols={2}>
                    <NumberInput
                      label="Min length"
                      size="xs"
                      min={0}
                      value={selectedField.validation?.minLength ?? ''}
                      onChange={(v) => updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        validation: { ...(f.validation ?? {}), minLength: v ? Number(v) : undefined },
                      }))}
                    />
                    <NumberInput
                      label="Max length"
                      size="xs"
                      min={0}
                      value={selectedField.validation?.maxLength ?? ''}
                      onChange={(v) => updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        validation: { ...(f.validation ?? {}), maxLength: v ? Number(v) : undefined },
                      }))}
                    />
                  </SimpleGrid>
                )}
                {selectedField.type === 'number' && (
                  <SimpleGrid cols={2}>
                    <NumberInput
                      label="Min value"
                      size="xs"
                      value={selectedField.validation?.min ?? ''}
                      onChange={(v) => updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        validation: { ...(f.validation ?? {}), min: v !== '' ? Number(v) : undefined },
                      }))}
                    />
                    <NumberInput
                      label="Max value"
                      size="xs"
                      value={selectedField.validation?.max ?? ''}
                      onChange={(v) => updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        validation: { ...(f.validation ?? {}), max: v !== '' ? Number(v) : undefined },
                      }))}
                    />
                  </SimpleGrid>
                )}
                {['text', 'textarea'].includes(selectedField.type) && (
                  <TextInput
                    label="Pattern (regex)"
                    size="xs"
                    value={selectedField.validation?.pattern ?? ''}
                    onChange={(e) => updateFieldInPage(selectedField.key, f => ({
                      ...f,
                      validation: { ...(f.validation ?? {}), pattern: e.target.value || undefined },
                    }))}
                    placeholder="e.g. ^[A-Z].*"
                  />
                )}
              </Stack>

              {/* 4. Conditional Logic */}
              <Divider my="sm" />
              <Text fw={600} size="xs" mb="xs">Conditional Logic</Text>
              <Stack gap="xs">
                <Select
                  label="Show when field"
                  size="xs"
                  clearable
                  placeholder="Always visible"
                  value={selectedField.conditional?.fieldKey ?? null}
                  data={allFields
                    .filter(f => f.key !== selectedField.key)
                    .map(f => ({ value: f.key, label: f.label || f.key }))
                  }
                  onChange={(val) => {
                    if (!val) {
                      updateFieldInPage(selectedField.key, f => {
                        const { conditional: _, ...rest } = f;
                        return rest as FormField;
                      });
                    } else {
                      updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        conditional: {
                          fieldKey: val,
                          operator: f.conditional?.operator ?? 'equals',
                          value: f.conditional?.value ?? '',
                        },
                      }));
                    }
                  }}
                />
                {selectedField.conditional && (
                  <>
                    <Select
                      label="Operator"
                      size="xs"
                      value={selectedField.conditional.operator}
                      data={OPERATOR_OPTIONS}
                      onChange={(val) => {
                        if (!val) return;
                        updateFieldInPage(selectedField.key, f => ({
                          ...f,
                          conditional: { ...(f.conditional ?? { fieldKey: '', operator: 'equals', value: '' }), operator: val as 'equals' | 'not_equals' | 'contains' | 'not_empty' | 'empty' },
                        }));
                      }}
                    />
                    {!['empty', 'not_empty'].includes(selectedField.conditional.operator) && (
                      <TextInput
                        label="Value"
                        size="xs"
                        value={selectedField.conditional.value}
                        onChange={(e) => {
                          updateFieldInPage(selectedField.key, f => ({
                            ...f,
                            conditional: { ...f.conditional!, value: e.target.value },
                          }));
                        }}
                      />
                    )}
                  </>
                )}
              </Stack>

              {/* 5. Type-specific config */}
              {selectedField.type === 'text' && (
                <>
                  <Divider my="sm" />
                  <Text fw={600} size="xs" mb="xs">Text Options</Text>
                  <Switch
                    label="Address autocomplete"
                    size="xs"
                    checked={!!selectedField.config.addressAutocomplete}
                    onChange={(e) => updateFieldInPage(selectedField.key, f => ({
                      ...f,
                      config: { ...f.config, addressAutocomplete: e.currentTarget.checked },
                    }))}
                  />
                </>
              )}
              {selectedField.type === 'likert' && (
                <>
                  <Divider my="sm" />
                  <Text fw={600} size="xs" mb="xs">Likert Scale</Text>
                  <TextInput
                    label="Scale columns (comma-separated)"
                    size="xs"
                    value={
                      Array.isArray(selectedField.config.likertColumns)
                        ? (selectedField.config.likertColumns as string[]).join(', ')
                        : ''
                    }
                    placeholder="e.g. Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree"
                    onChange={(e) => {
                      const cols = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                      updateFieldInPage(selectedField.key, f => ({
                        ...f,
                        config: { ...f.config, likertColumns: cols },
                      }));
                    }}
                  />
                </>
              )}

              {/* Remove Field */}
              <Divider my="sm" />
              <Button
                fullWidth
                variant="light"
                color="red"
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={() => removeField(selectedField.key)}
              >
                Remove Field
              </Button>
            </ScrollArea>
          ) : (
            <Center h="100%">
              <Stack align="center" gap="xs">
                <IconSettings size={32} color="var(--mantine-color-gray-4)" />
                <Text size="sm" c="dimmed" ta="center">
                  Select a field to configure it
                </Text>
              </Stack>
            </Center>
          )}
        </Paper>
      </div>
    </div>
  );
}
